import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Pencil } from "mdi-material-ui";

import { DataEditControl } from "../../DataEditing/DataEditControl";
import { IValueEditorMode } from "../../DataEditing/Common";

import {
    AddBoxRounded,
    ArrowDownwardOutlined,
    ArrowDropDownOutlined,
    ArrowDropUpOutlined,
    ArrowUpwardOutlined,
    DeleteOutlined,
} from "@mui/icons-material";
import {
    Button,
    ButtonGroup,
    Fade,
    FormControlLabel,
    FormLabel,
    IconButton,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from "@mui/material";
import Xarrow from "react-xarrows";

import { DataDefEditLayer } from "../DataDefEditLayer";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";

import { DataTypes } from "../../../rule-engine-core/Common/Data/DataTypes";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ObserveState } from "../../StateManagement/ObserveState";
import { ObjectDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";

import { EditModeObjectValueEditor } from "../../DataEditing/Object";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { StyledCheckBox } from "../../BuildlingBlocks/StyledCheckbox";
import { UILabel } from "../../BuildlingBlocks/UILabel";
import type { AppGlueAny } from "../../../rule-engine-core/Common/AppGlueAny";

export enum DefinitionWithExampleMode {
    VALUE = "value",
    DEFINITION = "definition",
}

interface IValuesTable {
    definition: DataDefinition;
    path: string;
    field: ObjectDataDefinition;
}

export const ValuesTable: React.FC<IValuesTable> = (props: IValuesTable) => {
    let value = DataUtilities.get(props.definition.getValueObject(), props.path);
    let allsubfields = DataUtilities.getAllSubFields(props.definition);
    let startRow = allsubfields.indexOf(props.field) + 1;

    const onValueUpdate = (newValue: AppGlueAny) => {
        props.definition.setValue(props.path, newValue);
        StateManager.changed(props.definition);
    };

    return (
        <ValuesTableWrapperDiv
            fieldsCount={DataUtilities.getAllSubFields(props.field).length}
            startRow={startRow}
        >
            <ValuesTableDiv>
                <EditModeObjectValueEditor
                    forData={props.field}
                    value={value}
                    editMode="inline"
                    onUpdate={onValueUpdate}
                    showFieldName={false}
                />
            </ValuesTableDiv>
        </ValuesTableWrapperDiv>
    );
};

interface IValueEditor {
    definition: DataDefinition;
    field: IDataDefinition;
    path: string;
    notHostEditLayer?: boolean;
}

const ValueEditor: React.FC<IValueEditor> = (props: IValueEditor) => {
    const value = DataUtilities.get(props.definition.getValueObject(), props.path);

    let editMode: IValueEditorMode = "edit_mode";
    if (props.field.list || props.field.type === DataTypes.FILE) {
        editMode = "display_mode_edit";
    }

    const onValueUpdate = (newValue: AppGlueAny) => {
        props.definition.setValue(props.path, newValue);
        StateManager.changed(props.definition);
    };

    return (
        <ValueEditorDiv>
            <ValueEditorValueDiv>
                <DataEditControl
                    forData={props.field}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={editMode}
                    editMode="layer"
                />
            </ValueEditorValueDiv>
        </ValueEditorDiv>
    );
};

interface ILineButtons {
    definition: DataDefinition;
    path: string;
    index: number;
    show: boolean;
    field: IDataDefinition;
}

const LineButtons: React.FC<ILineButtons> = (props: ILineButtons) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    let parent: DataDefinition | ObjectDataDefinition = props.definition;

    if (props.path !== "") {
        let paths = props.path.split(".");

        if (paths.length > 1) {
            let parentPath = paths.slice(0, -1).join(".");

            parent = DataUtilities.getFieldFromDataDefinitionByName(
                props.definition.fields,
                parentPath,
            ) as ObjectDataDefinition;
        }
    }

    let isLast = parent.fields.indexOf(props.field) === parent.fields.length - 1;

    const changePosition = (newindex: number) => {
        if (newindex < 0) return;
        DataUtilities.changeFieldPositionByPath(props.definition, newindex, props.path);
        StateManager.changed(props.definition);
    };

    const removeField = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        DataUtilities.removeFieldByPath(props.definition, props.path);
        StateManager.changed(props.definition);
    };

    return (
        <Fade in={props.show}>
            <LineButtonsDiv>
                <ButtonGroup variant="contained">
                    {props.index > 0 && (
                        <Button
                            size={"small"}
                            onClick={() => changePosition(props.index - 1)}
                            data-testid={`${props.path}-move-up`}
                        >
                            <ArrowUpwardOutlined fontSize="small" />
                        </Button>
                    )}
                    {!isLast && (
                        <Button
                            size={"small"}
                            onClick={() => changePosition(props.index + 1)}
                            data-testid={`${props.path}-move-down`}
                        >
                            <ArrowDownwardOutlined fontSize="small" />
                        </Button>
                    )}
                    <Button
                        size={"small"}
                        aria-label="delete"
                        onClick={removeField}
                        data-testid={`${props.path}-remove`}
                    >
                        <DeleteOutlined fontSize="small" />
                    </Button>
                    {props.field.type !== DataTypes.BOOLEAN && props.field.type !== DataTypes.UNDEFINED && (
                        <>
                            <Button
                                size={"small"}
                                aria-label="edit"
                                data-testid={`${props.path}-edit`}
                                onClick={(event: React.MouseEvent<HTMLElement>) => {
                                    setAnchorEl(event.currentTarget);
                                }}
                            >
                                <Pencil fontSize="small" />
                            </Button>
                            <DataDefEditLayer
                                dataDef={props.field}
                                onUpdate={() => {
                                    StateManager.changed(props.definition);
                                }}
                                open={Boolean(anchorEl)}
                                onClose={() => {
                                    setAnchorEl(null);
                                }}
                            />
                        </>
                    )}
                </ButtonGroup>
            </LineButtonsDiv>
        </Fade>
    );
};

interface FieldControlProps {
    definition: DataDefinition;
    field: IDataDefinition;
    path: string;
    viewDetails?: boolean;
    index: number;
    onCollapse?: () => void;
    readOnly?: boolean;
    mode: DefinitionWithExampleMode;
}

const FieldControl: React.FC<FieldControlProps> = (props: FieldControlProps) => {
    let field = props.field;
    const [fieldName, setFieldName] = useState<string>(field.name);
    const [showTools, setShowTools] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const isHideExampleDataSide =
        DataUtilities.containsObjectListField(props.definition) && props.mode === DefinitionWithExampleMode.DEFINITION;

    let showValueEditor = !isHideExampleDataSide;
    if (showValueEditor) {
        let paths = props.path.split(".").filter((p) => p !== "");
        paths.splice(-1, 1);

        for (let i = 1; i <= paths.length; i++) {
            let ancestorPath = paths.slice(0, i).join(".");

            let ancestorField = DataUtilities.getFieldFromDataDefinitionByName(
                props.definition.fields,
                ancestorPath,
            ) as IDataDefinition;

            if (ancestorField instanceof ObjectDataDefinition && ancestorField.list) {
                showValueEditor = false;
                break;
            }
        }
    }

    let showValuesTable = !isHideExampleDataSide && showValueEditor;
    if (showValuesTable) {
        showValuesTable = field.list && field instanceof ObjectDataDefinition;
    }

    useEffect(() => {
        let validationError = DataUtilities.validateDataDefinitionNameByPath(
            props.definition,
            props.path,
            fieldName,
            1,
        );
        if (!error && validationError) {
            setError(validationError);
        } else if (error && !validationError) {
            setError(null);
        }
    }, [error, props.definition, props.path, fieldName]);

    const onUpdateFieldName = () => {
        if (field.name !== fieldName) {
            try {
                DataUtilities.changeDataDefinitionNameByPath(props.definition, fieldName, props.path);
                StateManager.changed(props.definition);
                setError(null);
            } catch (e) {
                setError(`Can not set the field name with ${fieldName}`);
                console.error("Error:", e);
            }
        }
    };

    const onUpdateChangeType = (event: SelectChangeEvent<DataTypes>) => {
        try {
            DataUtilities.changeDataDefinitionTypeByPath(props.definition, event.target.value as DataTypes, props.path);
            StateManager.changed(props.definition);
        } catch (e) {}
    };

    let deepsCount = props.path.split(".").length - 1;

    return (
        <>
            <DataEditorRow
                onMouseOver={() => setShowTools(true)}
                onMouseOut={() => setShowTools(false)}
                deepsCount={deepsCount}
                showBorder={deepsCount === 0}
            >
                <CollapseDiv>
                    <div className="div-collapse">
                        {props.onCollapse && (
                            <IconButton
                                onClick={props.onCollapse}
                                size="small"
                            >
                                {props.viewDetails ? <ArrowDropUpOutlined /> : <ArrowDropDownOutlined />}
                            </IconButton>
                        )}
                    </div>
                </CollapseDiv>
                <DataEditorDefDiv>
                    <FieldNameDiv>
                        <TextField
                            value={fieldName}
                            variant="outlined"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldName(event.target.value);
                            }}
                            onBlur={onUpdateFieldName}
                            onKeyUp={(event: React.KeyboardEvent) => {
                                if (event.key === "Enter") {
                                    onUpdateFieldName();
                                }
                            }}
                            classes={{ root: "DefEditor-text" }}
                            error={!!error}
                            helperText={error}
                            data-testid={`${props.path}-name`}
                            id={`${props.path}-name`}
                            className="field-name"
                            inputProps={{ readOnly: props.readOnly }}
                            fullWidth
                        />
                        {props.path && (
                            <div
                                id={`${props.path}-name-connector`}
                                className="path-connector"
                            />
                        )}
                    </FieldNameDiv>
                    {!props.readOnly && (
                        <>
                            <Select
                                variant="outlined"
                                native
                                size={"small"}
                                value={field.type}
                                data-testid={`${props.path}-type`}
                                onChange={onUpdateChangeType}
                            >
                                {Object.values(DataTypes).map((item, index) => {
                                    return (
                                        <option
                                            value={item}
                                            key={index}
                                        >
                                            {item}
                                        </option>
                                    );
                                })}
                            </Select>
                            <FormControlLabel
                                label="Is List"
                                data-testid={`${props.path}-list`}
                                classes={{ root: "DefEditor-checkboxLabel" }}
                                control={
                                    <StyledCheckBox
                                        checked={!!field.list}
                                        onChange={(_: React.ChangeEvent<{}>, checked: boolean) => {
                                            field.list = checked;
                                            if (checked) {
                                                DataUtilities.set(
                                                    props.definition.getValueObject(),
                                                    props.path,
                                                    [],
                                                    false,
                                                    true,
                                                );
                                            } else {
                                                DataUtilities.set(
                                                    props.definition.getValueObject(),
                                                    props.path,
                                                    undefined,
                                                    false,
                                                    true,
                                                );
                                            }
                                            StateManager.changed(props.definition);
                                        }}
                                    />
                                }
                            />
                        </>
                    )}
                    {props.readOnly && (
                        <>
                            <div className="data-field-type">
                                <span>{field.type}</span>
                            </div>
                            <FormLabel
                                component="label"
                                className="data-field-list"
                            >
                                {field.list ? "List" : "Not List"}
                            </FormLabel>
                        </>
                    )}
                    {!props.readOnly && (
                        <LineButtons
                            definition={props.definition}
                            path={props.path}
                            index={props.index}
                            show={showTools}
                            field={props.field}
                        />
                    )}
                </DataEditorDefDiv>
            </DataEditorRow>
            {showValueEditor && (
                <ValueEditor
                    field={field}
                    definition={props.definition}
                    path={props.path}
                />
            )}
        </>
    );
};

interface FieldEditorProps {
    definition: DataDefinition;
    path: string;
    field: IDataDefinition;
    index: number;
    readOnly?: boolean;
    mode: DefinitionWithExampleMode;
}

const FieldEditor: React.FC<FieldEditorProps> = (props: FieldEditorProps) => {
    let field = props.field;
    let subfields = Reflect.get(field, "fields") as IDataDefinition[];
    let path = props.path;

    let parentPath: string | undefined;

    if (path !== "") {
        let paths = path.split(".");
        if (paths.length > 1) {
            parentPath = paths.slice(0, -1).join(".");
        }
    }

    const [viewDetails, setViewDetails] = useState<boolean>(true);

    const onCollapse = () => {
        setViewDetails(!viewDetails);
    };

    const isHideExampleDataSide =
        DataUtilities.containsObjectListField(props.definition) && props.mode === DefinitionWithExampleMode.DEFINITION;

    if (subfields) {
        let showValueEditor = !isHideExampleDataSide;
        if (showValueEditor) {
            let paths = props.path.split(".").filter((p) => p !== "");
            paths.splice(-1, 1);

            for (let i = 1; i <= paths.length; i++) {
                let ancestorPath = paths.slice(0, i).join(".");

                let ancestorField = DataUtilities.getFieldFromDataDefinitionByName(
                    props.definition.fields,
                    ancestorPath,
                ) as IDataDefinition;

                if (ancestorField instanceof ObjectDataDefinition && ancestorField.list) {
                    showValueEditor = false;
                    break;
                }
            }
        }

        let showValuesTable = !isHideExampleDataSide && showValueEditor;
        if (showValuesTable) {
            showValuesTable = field.list && field instanceof ObjectDataDefinition;
        }

        return (
            <>
                <FieldControl
                    field={field}
                    path={props.path}
                    definition={props.definition}
                    viewDetails={viewDetails}
                    index={props.index}
                    onCollapse={onCollapse}
                    readOnly={props.readOnly}
                    mode={props.mode}
                />

                {viewDetails && (
                    <>
                        <SubFieldsEditor
                            definition={props.definition}
                            path={props.path}
                            fields={subfields}
                            readOnly={props.readOnly}
                            mode={props.mode}
                        />
                        {showValuesTable && (
                            <ValuesTable
                                field={props.field as ObjectDataDefinition}
                                definition={props.definition}
                                path={props.path}
                            />
                        )}
                    </>
                )}

                {parentPath && parentPath !== "" && (
                    <Xarrow
                        end={`${parentPath}-name-connector`}
                        start={`${props.path}-name`}
                        startAnchor="left"
                        endAnchor="bottom"
                        path="grid"
                        showHead={false}
                        showTail={false}
                        color={UIStyleConstants.GRAY_VERY_LIGHT_COLOR}
                        strokeWidth={1}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <FieldControl
                definition={props.definition}
                field={field}
                path={props.path}
                index={props.index}
                readOnly={props.readOnly}
                mode={props.mode}
            />
            {parentPath && parentPath !== "" && (
                <Xarrow
                    end={`${parentPath}-name-connector`}
                    start={`${props.path}-name`}
                    startAnchor="left"
                    path="grid"
                    showHead={false}
                    showTail={false}
                    endAnchor="bottom"
                    color={UIStyleConstants.GRAY_VERY_LIGHT_COLOR}
                    strokeWidth={1}
                />
            )}
        </>
    );
};

interface SubFieldsEditorProps {
    definition: DataDefinition;
    path?: string;
    fields: IDataDefinition[];
    readOnly?: boolean;
    mode: DefinitionWithExampleMode;
}

const SubFieldsEditor: React.FC<SubFieldsEditorProps> = (props: SubFieldsEditorProps) => {
    const { definition, fields, path } = props;
    const [newFieldName, setNewFieldName] = useState<string>("");

    let prefixPath = path ? `${path}.` : "";

    const [error, setError] = useState<string | null>(null);

    const isHideExampleDataSide =
        DataUtilities.containsObjectListField(props.definition) && props.mode === DefinitionWithExampleMode.DEFINITION;

    const createNewField = () => {
        try {
            DataUtilities.addDataDefinitionByPath(
                props.definition,
                DataTypes.UNDEFINED,
                props.path || "",
                newFieldName,
            );
            setNewFieldName("");
            StateManager.changed(definition);
            if (error) {
                setError(null);
            }
        } catch (e: AppGlueAny) {
            setError(e.message);
        }
    };

    return (
        <>
            {fields.map((field, idx) => {
                return (
                    <FieldEditor
                        key={`${field.name}_${idx}`}
                        definition={props.definition}
                        field={field}
                        path={`${prefixPath}${field.name}`}
                        index={idx}
                        data-testid={`${prefixPath}${field.name}`}
                        readOnly={props.readOnly}
                        mode={props.mode}
                    />
                );
            })}
            {!props.readOnly && (
                <>
                    <AddNewField
                        className="add-new-field"
                        deepsCount={props.path ? props.path.split(".").length + 1 : 1}
                        showBorder={prefixPath === ""}
                    >
                        <Button onClick={createNewField}>
                            <AddBoxRounded fontSize="large"></AddBoxRounded>
                        </Button>
                        <div
                            className="add-new-field-name"
                            onClick={createNewField}
                        ></div>
                        <div
                            className="add-new-field-type"
                            onClick={createNewField}
                        ></div>
                        <div
                            className="add-new-field-list"
                            onClick={createNewField}
                        ></div>
                    </AddNewField>
                    {!isHideExampleDataSide && <EmptyValueField></EmptyValueField>}
                </>
            )}
        </>
    );
};

interface DefinitionWithExampleProps {
    definition: DataDefinition;
    readOnly?: boolean;
    // it shows what is focus value or definition.
    mode?: DefinitionWithExampleMode;
}

export const DefinitionWithExample = (props: DefinitionWithExampleProps) => {
    const { definition, readOnly, mode = DefinitionWithExampleMode.DEFINITION } = props;

    const isHideExampleDataSide =
        DataUtilities.containsObjectListField(props.definition) && mode === DefinitionWithExampleMode.DEFINITION;

    return (
        <DefinitionWithExampleRender>
            <UILabel
                sx={{
                    borderRight: "1px solid " + UIStyleConstants.BLUE_LIGHT_COLOR,
                    background: UIStyleConstants.GRAY_VERY_LIGHT_COLOR,
                }}
                variant={"title"}
                paddingTop={"10px"}
                paddingBottom={"10px"}
                paddingLeft={"10px"}
                color={"UIBlue"}
            >
                Schema
            </UILabel>
            <UILabel
                sx={{
                    background: UIStyleConstants.GRAY_VERY_LIGHT_COLOR,
                }}
                variant={"title"}
                paddingTop={"10px"}
                paddingBottom={"10px"}
                paddingLeft={"10px"}
                color={"UIBlue"}
            >
                Example Data
            </UILabel>

            <ObserveState
                listenTo={definition}
                control={() => {
                    return (
                        <>
                            <SubFieldsEditor
                                definition={definition}
                                fields={definition.fields}
                                readOnly={readOnly || mode === DefinitionWithExampleMode.VALUE}
                                mode={mode}
                            />
                            {isHideExampleDataSide && (
                                <NotificationRender rowsSpan={100000}>
                                    <Typography
                                        variant="h5"
                                        color={UIStyleConstants.BLUE_COLOR}
                                    >
                                        Edit in example data tab
                                    </Typography>
                                </NotificationRender>
                            )}
                        </>
                    );
                }}
            />
        </DefinitionWithExampleRender>
    );
};

const DefinitionWithExampleRender = styled.div`
    display: grid;

    position: relative;

    grid-template-columns: 600px minmax(400px, auto);

    flex: 1;
    overflow: auto;
`;

const LineButtonsDiv = styled.div`
    position: absolute;
    top: -15px;
    right: 0;
    z-index: 10;
`;

const DataEditorRow = styled.div<{ deepsCount?: number; showBorder: boolean }>`
    display: flex;
    flex-direction: row;
    position: relative;
    min-height: 52px;
    box-sizing: border-box;

    grid-column-start: 1;
    grid-column-end: 2;

    ${(props) =>
        props.showBorder
            ? `
                border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
            `
            : ``}

    border-right: 1px solid ${UIStyleConstants.BLUE_LIGHT_COLOR};

    ${(props) => (props.deepsCount ? `padding-left: ${props.deepsCount * 30}px;` : ``)}

    min-width: 600px;
    width: 600px;

    padding-right: 15px;

    .data-field-type {
        min-width: 70px;
        text-align: center;
        text-transform: capitalize;
        background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
        padding: 10px 0px;
        border-radius: 4px;
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    }

    .data-field-list {
        min-width: 90px;
        font-size: 14px;
        text-align: center;
        text-transform: capitalize;
        background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
        padding: 10px 0px;
        border-radius: 4px;
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        align-items: center;
        display: flex;
        justify-content: center;
    }
`;

const CollapseDiv = styled.div`
    width: 30px;
    margin-right: 15px;
    display: flex;
    align-items: start;

    .div-collapse {
        display: flex;
        align-items: center;
        width: 100%;
        height: 40px;
        background: ${UIStyleConstants.WHITE_COLOR};
        border-radius: 5px;
        margin-top: 5px;
    }
`;

const DataEditorDefDiv = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    width: 100%;

    > * {
        margin: 5px !important;
    }

    .DefEditor-select,
    .DefEditor-text .MuiOutlinedInput-input {
        padding: 10px;
        background: ${UIStyleConstants.WHITE_COLOR};
    }
`;

const FieldNameDiv = styled.div`
    flex: 1;
    position: relative;

    .path-connector {
        position: absolute;
        bottom: -5px;
        left: 15px;
        width: 5px;
        height: 5px;
    }
`;

const ValueEditorDiv = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    align-items: center;
    flex-direction: row;
    padding-left: 15px;
    padding-right: 15px;

    border-top: solid 1px ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    border-left: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};

    .input-value-field {
        width: 400px;
    }

    .input-value-field input {
        background: ${UIStyleConstants.WHITE_COLOR};
    }
`;

const ValueEditorValueDiv = styled.div`
    width: 100%;
    padding: 0 5px;
    display: flex;
    align-items: center;
    min-height: 52px;

    .MuiTypography-body1 {
        line-height: 29px;
    }
`;

const NotificationRender = styled.div<{ rowsSpan: number }>`
    grid-column-start: 2;
    grid-column-end: 3;

    grid-row-start: 2;
    grid-row-end: ${(props) => props.rowsSpan};

    padding-top: 150px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
`;

const AddNewField = styled.div<{ deepsCount: number; showBorder: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    padding-left: ${(props) => props.deepsCount * 30 - 15}px;

    ${(props) => (props.showBorder ? `border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};` : ``)}

    border-right: 1px solid ${UIStyleConstants.BLUE_LIGHT_COLOR};

    padding-right: 15px;

    .MuiButton-root {
        min-width: 34px;
        padding: 0;
        margin: 5px 0;
        color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }

    div {
        background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
        border-radius: 5px;
        margin: 5px 0;
    }

    .add-new-field-name {
        width: calc(100% - 250px);
    }

    .add-new-field-type {
        width: 93px;
    }

    .add-new-field-list {
        width: 97px;
    }

    &:hover {
        cursor: pointer;
    }
`;

const ValuesTableWrapperDiv = styled.div<{ fieldsCount: number; startRow: number }>`
    position: relative;
    grid-column-start: 2;
    grid-column-end: 3;

    overflow: auto;

    grid-row-start: ${(props) => props.startRow + 2};
    grid-row-end: ${(props) => props.startRow + 2 + props.fieldsCount};

    border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    border-left: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
`;

const ValuesTableDiv = styled.div<{ height?: number }>`
    display: flex;
    align-items: flex-start;
    padding: 10px;
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};

    .MuiTypography-h6 {
        margin-right: 10px;
    }

    .MuiInputBase-input,
    .MuiInputBase-input,
    .MuiOutlinedInput-input {
        font-family: Inter !important;
        font-size: 12px !important;
        font-style: normal;
        font-weight: 400 !important;
        line-height: 17px !important;
        letter-spacing: 0px;
        text-align: left;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    }

    .MuiOutlinedInput-input {
        padding: 6px 12px;
        background: ${UIStyleConstants.GRAY_COLOR};
        height: 20px;
    }

    .MuiOutlinedInput-notchedOutline {
        border: none;
        border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 0;
    }

    .MuiFormControl-root {
        display: flex;
    }

    .MuiSwitch-root {
        height: 25px !important;
        width: 40px !important;
        padding: 0;
        border-radius: 20px;
    }

    .MuiSwitch-switchBase {
        padding: 2px !important;

        &.Mui-checked {
            transform: translateX(15px) !important;
        }
    }

    input[type="date"],
    input[type="datetime-local"] {
        position: relative;

        &::-webkit-calendar-picker-indicator {
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            height: 20px;
            width: 20px;
            opacity: 0;
        }

        &:hover::-webkit-calendar-picker-indicator {
            opacity: 1;
        }
    }

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */

    input[type="number"] {
        -moz-appearance: textfield;
    }
`;

const EmptyValueField = styled.div`
    border-top: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    border-left: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
`;
